import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  ColumnDef,
  functionalUpdate,
  getExpandedRowModel,
  TableOptions,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { EventAccordionItemBodyComponentType } from 'src/components/Accordions';
import { shiftSelect } from 'src/components/Events/EventPage/EventPage.css';
import { ListingDisplay } from 'src/components/Listings/ListingDisplay';
import {
  createAggregatedListing,
  createFakeListingGroup,
} from 'src/components/Listings/listingUtils';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { MultiSelectScope } from 'src/contexts/MultiSelectionContext';
import { Stack } from 'src/core/ui';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useSortListingTableSeatingColumn } from 'src/hooks/useSortListingTableSeatingColumn';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOption, FormatOptionEntries } from 'src/modals/EditTableColumns';
import {
  flattenListingGroup,
  sortGroupType,
} from 'src/modals/GroupListings/components/groupingUtils';
import { GroupIdNameMap } from 'src/modals/MergeListings/utils';
import { inventoryEventDetailsTableCollapsed } from 'src/tables/ListingTable/ListingTable.css';
import { NoData, Table } from 'src/tables/Table';
import {
  defaultListingColumnsConfig,
  filterColumnsByFeatures,
  getListingColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { LISTING_MAIN_COLUMNS } from 'src/utils/columns/inventory/inventoryColumnUtils.constants';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { filterCustomColumnsForListing } from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  GroupType,
  Listing,
  ListingGroup,
  ListingStatus,
  UserSetting,
} from 'src/WebApiController';

import {
  customColumnDef,
  getListingTableColumnConfigs,
  tagColumnDef,
} from './configs/ListingTableColumnsConfig';
import * as styles from './ListingTable.css';
import {
  getRowId,
  useRowDragAndDrop,
  useRowOperations,
  useTableData,
  useTableState,
} from './ListingTable.hooks';
import { ListingWithEvent } from './ListingTable.types';

export type ListingTableProps = EventAccordionItemBodyComponentType<Listing> & {
  getListingNotEligibleForSelection?: (
    listings: Listing[] | null
  ) => { listing: Listing; ineligibleReason: string | undefined }[] | undefined;
  showMainColumnsOnly?: boolean;
  hideUnselectableListings?: boolean;
  showUnselectableListingsOnly?: boolean;
  disablePagination?: boolean;
  compListings?: Listing[];
  getListingTicketClassColor?: (listing: Listing) => string | undefined;
  listingGroupMap?: GroupIdNameMap;
  isSideTable?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
};

export const PAGE_SIZE = 10;

export const ListingTable = ({
  event,
  entities: listings,
  entitiesSummary: listingsSummary,
  compListings,
  entityCount: listingCount,
  ungroupedListingCount,
  listingGroupCount,
  getDataFail,
  selectedIds,
  selectionMode: defaultSelectionMode,
  onItemSelectionsChanged,
  getListingNotEligibleForSelection,
  hideUnselectableListings,
  showUnselectableListingsOnly,
  showMainColumnsOnly,
  disablePagination,
  onMount,
  onUnmount,
  initState,
  useVirtuoso,
  getListingTicketClassColor,
  tableWithOuterPadding,
  useTableNavKeys = false,
  listingGroupMap,
  isSideTable,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  enableColumnFilters,
}: ListingTableProps) => {
  const enableSelectionByDefault = useEnableSelectionByDefault();
  alwaysShowCheckbox = alwaysShowCheckbox || enableSelectionByDefault;

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Listing
  );
  const hasTableRowSelectionV2 = useUserHasFeature(Feature.TableRowSelectionV2);
  const hasReplaceUnsoldWithAvailableTicketsFeature = useUserHasFeature(
    Feature.ReplaceUnsoldWithAvailableTickets
  );

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const { appContext, loginContext } = useAppContext();
  const listingText = useContent(ContentId.Listings);
  const isItemsLoading =
    listingCount > 0 && (listings == null || listings.length == 0);
  const hasSortListingsInGroupFeature = useUserHasFeature(
    Feature.SortListingsInGroup
  );

  const hasSurfaceSurfaceFirstListingInGroupFeature = useUserHasFeature(
    Feature.SurfaceFirstListingInGroup
  );

  const soldGroupName = useContent(ContentId.Sold);
  const isGroupSoldListingsEnabled = useUserHasFeature(
    Feature.GroupSoldListings
  );

  const { filteredListings, mapListingWithEvent } = useTableData(
    event,
    listings,
    compListings,
    listingGroupMap,
    hideUnselectableListings,
    showUnselectableListingsOnly,
    getListingNotEligibleForSelection,
    getListingTicketClassColor
  );

  const data = useMemo<(ListingWithEvent | null)[]>(() => {
    let result = filteredListings;

    if (isGroupSoldListingsEnabled && result) {
      const ungroupedSoldListings = result.filter(
        (listing) =>
          !!listing &&
          !listing.isLtGrp &&
          !listing.listing?.ltGrpId &&
          listing?.listing?.status === ListingStatus.FullySold
      );

      if (ungroupedSoldListings.length) {
        const fakeListingGroup = createFakeListingGroup(
          ungroupedSoldListings,
          soldGroupName
        );
        if (fakeListingGroup) {
          result = [
            ...result.filter((d) => !ungroupedSoldListings.includes(d)),
            fakeListingGroup,
          ];
        }
      }
    }

    return result?.length
      ? result
      : result == null || isItemsLoading
        ? (generateShimmeringRows(
            ungroupedListingCount ?? 1,
            listingGroupCount ?? 0
          ) as (ListingWithEvent | null)[])
        : result;
  }, [
    filteredListings,
    isGroupSoldListingsEnabled,
    soldGroupName,
    isItemsLoading,
    ungroupedListingCount,
    listingGroupCount,
  ]);

  const {
    sorting,
    setSorting,
    expandedState,
    setExpandedState,
    pagination,
    setPagination,
  } = useTableState(
    event,
    listings,
    listingCount,
    data,
    disablePagination,
    initState,
    onMount,
    onUnmount
  );

  const {
    selectionMode,
    showCheckbox,
    visibleRowIds,
    rowSelection,
    listingSelection,
    isShiftKeyPressed,
    onRowSelect,
    onRowWrapper,
    onRowHotkeySelect,
    onRowShiftSelect,
    onFilteredRowsChange,
    onRowSelectionChange,
  } = useRowOperations(
    event,
    data,
    pagination,
    selectedIds,
    defaultSelectionMode,
    isSideTable,
    alwaysShowCheckbox,
    onItemSelectionsChanged
  );

  const {
    value: storedInventoryColumnOrderSetting = defaultListingColumnsConfig,
  } = useServerUserSetting<ListingTableColumnId[]>({
    id: isSideTable
      ? UserSetting.InventorySideTableColumnOrder
      : UserSetting.InventoryColumnOrder,
  });
  const { value: storedInventoryColumnNumberPrecisions = {} } =
    useServerUserSetting<FormatOptionEntries>({
      id: isSideTable
        ? UserSetting.InventorySideTableColumnNumberPrecision
        : UserSetting.InventoryColumnNumberPrecision,
    });

  const { value: storedInventoryColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: isSideTable
      ? UserSetting.InventorySideTableColumnsEnabled
      : UserSetting.InventoryColumnsEnabled,
  });

  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: UserSetting.InventoryCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Listing,
    true
  );

  // With replacing column feature, we want to sanitized the settings and replace the column in the user settings too
  const [sanitizedInventoryColumnOrder, sanitizedInventoryColumnEnabled] =
    useMemo(() => {
      if (!hasReplaceUnsoldWithAvailableTicketsFeature) {
        return [
          storedInventoryColumnOrderSetting,
          storedInventoryColumnsEnabledSetting,
        ];
      }
      const sanitizedColumnOrder: ListingTableColumnId[] =
        storedInventoryColumnOrderSetting.map((columnId) =>
          columnId === ListingTableColumnId.UnsoldQuantity
            ? ListingTableColumnId.AvailableQuantity
            : columnId
        );
      const sanitizedColumnEnabled = storedInventoryColumnsEnabledSetting?.map(
        (columnId) =>
          columnId === ListingTableColumnId.UnsoldQuantity
            ? ListingTableColumnId.AvailableQuantity
            : columnId
      );
      return [sanitizedColumnOrder, sanitizedColumnEnabled];
    }, [
      hasReplaceUnsoldWithAvailableTicketsFeature,
      storedInventoryColumnOrderSetting,
      storedInventoryColumnsEnabledSetting,
    ]);

  const customListingColumnsFiltered = filterCustomColumnsForListing(
    customListingColumns,
    tagsMetadataNumeric
  );

  const hasGroup = useMemo(
    () => listings?.find((l) => l.isLtGrp) != null,
    [listings]
  );

  const allColumnConfigs = useMemo(
    () =>
      getListingTableColumnConfigs(
        hasSortListingsInGroupFeature,
        hasGroup,
        hasSurfaceSurfaceFirstListingInGroupFeature
      ),
    [
      hasGroup,
      hasSortListingsInGroupFeature,
      hasSurfaceSurfaceFirstListingInGroupFeature,
    ]
  );

  const displayedColumnsConfig = useMemo(() => {
    if (showMainColumnsOnly) {
      return LISTING_MAIN_COLUMNS.filter((c) => {
        if (c === ListingTableColumnId.Checkbox) {
          return showCheckbox;
        }
        // Only show Seating Row and Seats if Seating is set to Section Only
        if (
          c === ListingTableColumnId.SeatingRow ||
          c === ListingTableColumnId.SeatingSeats
        ) {
          return (
            storedInventoryColumnNumberPrecisions[
              ListingTableColumnId.Seating
            ] === FormatOption.Seating_SectionOnly
          );
        }

        if (c === ListingTableColumnId.ConsigneeSellerAccountId) {
          return hasConsignorMasterUI;
        }
        return true;
      }).reduce<ColumnDef<ListingWithEvent | null>[]>((acc, columnId) => {
        const columnConfig = Object.values(allColumnConfigs).find(
          (column) => column.id === columnId
        );
        if (columnConfig) {
          acc.push(columnConfig as ColumnDef<ListingWithEvent | null>);
        }
        return acc;
      }, []);
    }

    const inventoryColumnOrder = sanitizedInventoryColumnOrder.filter(
      // If storedInventoryColumnsEnabledSetting has never been set, default to true
      (c) => sanitizedInventoryColumnEnabled?.includes(c) ?? true
    );

    const columns = new Set([
      // Add unconfigurable columns first
      ...Object.values(allColumnConfigs)
        .filter((column) => {
          const columnDef = getListingColumnConfigById(
            column.id as ListingTableColumnId
          ).personalization;

          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures
            );

          const isConfigurable = columnDef.isConfigurable;
          return !isConfigurable && hasFeatureForColumn;
        })
        .map((item) => item.id as ListingTableColumnId),
      // Add user defined columns next
      ...filterColumnsByFeatures(
        inventoryColumnOrder,
        SectionType.Listings,
        customListingColumnsFiltered,
        loginContext?.user,
        appContext?.features,
        tagsMetadata ?? []
      ),
    ]);

    if (showCheckbox) {
      if (!alwaysShowCheckbox) {
        columns.delete(ListingTableColumnId.Actions);
      }
    } else {
      columns.delete(ListingTableColumnId.Checkbox);
    }

    return Array.from(columns).reduce<ColumnDef<ListingWithEvent | null>[]>(
      (acc, columnId) => {
        if (tagsMetadata?.find((t) => t.key === columnId)) {
          acc.push(tagColumnDef(columnId));
        } else if (
          customListingColumnsFiltered.find((c) => c.id === columnId)
        ) {
          acc.push(
            customColumnDef(
              columnId,
              customListingColumnsFiltered.find((c) => c.id === columnId)
                ?.formula
            )
          );
        } else {
          const columnConfig = Object.values(allColumnConfigs).find(
            (column) => column.id === columnId
          );
          if (columnConfig) {
            acc.push(columnConfig as ColumnDef<ListingWithEvent | null>);
          }
        }

        return acc;
      },
      []
    );
  }, [
    showMainColumnsOnly,
    sanitizedInventoryColumnOrder,
    allColumnConfigs,
    customListingColumnsFiltered,
    loginContext?.user,
    appContext?.features,
    tagsMetadata,
    showCheckbox,
    storedInventoryColumnNumberPrecisions,
    hasConsignorMasterUI,
    sanitizedInventoryColumnEnabled,
    alwaysShowCheckbox,
  ]);

  const sectionType = isSideTable
    ? SectionType.InventorySideTable
    : SectionType.Listings;

  const seatingColumnSorting = useSortListingTableSeatingColumn(sectionType);

  // Memoize heavy state objects separately
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility: {
        [ListingTableColumnId.TicketClassColor]:
          getListingTicketClassColor != null,
      },
      columnPinning: {
        right: [ListingTableColumnId.Actions],
      },
    }),
    [
      pagination,
      sorting,
      rowSelection,
      expandedState,
      getListingTicketClassColor,
    ]
  );

  const listingsSummaryFiltered = useMemo(() => {
    if (!listingsSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
      return createAggregatedListing(
        data
          ?.filter((l) => !l?.isCompListing)
          ?.flatMap((l) => flattenListingGroup(l?.listing))
          .filter((l) => l?.isFull)
          .map((l) =>
            mapListingWithEvent({
              listing: l as Listing,
              ineligibleReason: undefined,
            })
          )
          .filter(
            (d, i) =>
              d?.listing?.isFull &&
              visibleRowIds.includes(Number(getRowId(d, i)))
          )
          .map((d) => d!)
      );
    }
    return mapListingWithEvent({
      listing: listingsSummary,
      ineligibleReason: undefined,
    });
  }, [
    listingsSummary,
    visibleRowIds,
    mapListingWithEvent,
    data,
    hasTableTotalsFeature,
  ]);

  const tableMeta = useMemo(
    () => ({
      sectionType,
      showSelectAllCheckbox: showSelectAllOnHeader,
      summaryData: listingsSummaryFiltered,
    }),
    [sectionType, showSelectAllOnHeader, listingsSummaryFiltered]
  );

  const options: Partial<TableOptions<ListingWithEvent | null>> = useMemo(
    () => ({
      data,
      columns: displayedColumnsConfig,
      state: tableState,
      meta: tableMeta,

      getRowId,
      onPaginationChange: setPagination,
      onSortingChange: (sortingUpdaterFn) => {
        const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
        setSorting(newSortVal);
      },
      onExpandedChange: (newState) => {
        setExpandedState(newState);
      },
      getSubRows: (originalRow) => {
        if (!originalRow?.listing || !originalRow.listing.isLtGrp) {
          return [];
        }
        const listingGroup = originalRow.listing as ListingGroup;
        return (
          [...(listingGroup.groupItems ?? [])]
            .toSorted((a, b) => {
              if (a.isLtGrp && b.isLtGrp) {
                return sortGroupType(
                  (a as ListingGroup).groupType,
                  (b as ListingGroup).groupType
                );
              } else if (a.isLtGrp || b.isLtGrp) {
                return a.isLtGrp ? -1 : 1; // listing group sort first
              }

              return (a.ltGrpPrior ?? -1) - (b.ltGrpPrior ?? -1);
            })
            ?.map(
              (l, i) =>
                ({
                  ...originalRow,
                  rowIndex: i,
                  listing: l,
                  isLtGrp: l.isLtGrp,
                }) as ListingWithEvent
            ) ?? []
        );
      },
      getRowCanExpand: (originalRow) => {
        return Boolean(
          originalRow.original?.listing?.isLtGrp &&
          (originalRow.original?.listing as ListingGroup).groupType !==
            GroupType.Invisible
        );
      },
      getExpandedRowModel: (table) => getExpandedRowModel()(table),
      autoResetExpanded: false,
      paginateExpandedRows: false,
      enableRowSelection: (row) => !row.original?.isCompListing,
      enableMultiRowSelection: (row) => !row.original?.isCompListing,
      onRowSelectionChange,
      enableSubRowSelection: (row) => !row.original?.isCompListing,
      sortingFns: { ...seatingColumnSorting },
    }),
    [
      data,
      displayedColumnsConfig,
      tableState,
      tableMeta,
      setPagination,
      onRowSelectionChange,
      seatingColumnSorting,
      sorting,
      setSorting,
      setExpandedState,
    ]
  );

  const { sensors, listingsToDrop, onDragListingEnd, onDragListingStart } =
    useRowDragAndDrop(
      listings,
      showCheckbox,
      rowSelection,
      selectedIds,
      setExpandedState,
      onRowSelectionChange
    );

  return getDataFail ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : data?.length ? (
    <DndContext
      sensors={sensors}
      onDragStart={onDragListingStart}
      onDragEnd={onDragListingEnd}
    >
      <ColumnResizingContextProvider
        userSettingId={UserSetting.InventoryColumnWidths}
      >
        <Table
          withOuterPadding={tableWithOuterPadding}
          options={options}
          rowWrapper={onRowWrapper}
          tableLayout="fixed"
          useStickyHeader={disablePagination}
          useVirtuoso={useVirtuoso}
          className={clsx({
            [inventoryEventDetailsTableCollapsed]:
              selectionMode?.mode === 'singleGroup',
            [shiftSelect]: isShiftKeyPressed,
          })}
          useTableNavKeys={
            useTableNavKeys &&
            !(
              selectionMode?.mode === MultiSelectScope.AllGroups ||
              listingSelection.isSingleGroupMultiSelect
            )
          }
          onRowHotkeySelect={onRowHotkeySelect}
          activeRowIds={new Set(selectedIds?.map((id) => id.toString()))}
          onRowSelect={onRowSelect}
          onRowShiftSelect={onRowShiftSelect}
          enableColumnFilters={enableColumnFilters}
          onFilteredRowsChange={
            enableColumnFilters ? onFilteredRowsChange : undefined
          }
          disableFooter={!listingsSummaryFiltered}
          useStickyFooter
          rowSelectionV2={hasTableRowSelectionV2}
        />
        {createPortal(
          <DragOverlay>
            {!isEmpty(listingsToDrop) ? (
              <div className={styles.seatDndOverlay}>
                <Stack
                  direction="column"
                  alignItems="center"
                  width="full"
                  style={{ overflow: 'hidden' }}
                  gap="s"
                >
                  {listingsToDrop.map((l) => (
                    <div key={l.id} className={styles.dndDisplayContainer}>
                      <ListingDisplay
                        listing={l}
                        width="full"
                        justifySeatingIconContent="spaceBetween"
                        allowDisplaySeatingOverride={true}
                      />
                    </div>
                  ))}
                </Stack>
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </ColumnResizingContextProvider>
    </DndContext>
  ) : (
    // We should never see this - because the events are filtered to only those that has listings
    // So this is to prevent a crash - but this should be looked at
    <NoData>
      <FormatContent
        id={FormatContentId.NoDataAvailable}
        params={listingText}
      />
    </NoData>
  );
};

export default ListingTable;
